import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Cart drawer behaviour inside `<theme-drawer>` (auto-open on add, sticky summary, installments CTA).
 *
 * MAGI 30+ (22 Oct 2026):
 * - Opens the moment Add to Cart is tapped, even on an empty cart (dialog[data-adding] spinner).
 * - dialog[data-just-added] shows "Added to your cart!". Cleared on close.
 * - After an add, the offer box (top of the drawer) is held hidden (dialog[data-offer-hold]) so the cart
 *   first shows only "Added" + products. ~0.65s later a space opens at the top, pushing them down,
 *   and the box pops in. Confetti + "unlocked!" when data-stage goes up (after the pop).
 * - Opening from the bag icon: no animation.
 * - Offer button (.mfs__btn) goes to New Arrivals; cart closes after the new page loads
 *   (sessionStorage 'magiCloseCartAfterNav'). Already on that page: just closes.
 * Snippets: magi-free-shipping-bar.liquid, cart-drawer.liquid.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;
  /** @type {number | undefined} */
  #addingTimeout;
  /** @type {number | undefined} */
  #holdTimeout;
  #lastStage = -1;
  #pendingEnter = false;
  #pendingCelebrate = 0;
  #enteredAt = 0;
  /** @type {MutationObserver | null} */
  #observer = null;
  #visRaf = 0;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#dialog?.addEventListener('close', this.#clearJustAdded);
    this.addEventListener('click', this.#handleOfferClick);
    this.addEventListener('scroll', this.#scheduleOfferVisibility, true);
    window.addEventListener('resize', this.#scheduleOfferVisibility);

    if (!document.getElementById('magi-offer-hold-css')) {
      const style = document.createElement('style');
      style.id = 'magi-offer-hold-css';
      style.textContent = 'dialog[data-offer-hold] .mfs-offer{display:none !important;}';
      document.head.appendChild(style);
    }

    this.#lastStage = this.#readStage();
    this.#observer = new MutationObserver(this.#checkOffer);
    this.#observer.observe(this, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-stage'] });

    // The restore path sets [open] before this module loads.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }

    this.#closeAfterOfferNavigation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#dialog?.removeEventListener('close', this.#clearJustAdded);
    this.removeEventListener('click', this.#handleOfferClick);
    this.removeEventListener('scroll', this.#scheduleOfferVisibility, true);
    window.removeEventListener('resize', this.#scheduleOfferVisibility);
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#addingTimeout) clearTimeout(this.#addingTimeout);
    if (this.#holdTimeout) clearTimeout(this.#holdTimeout);
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#scheduleOfferVisibility();
    setTimeout(this.#scheduleOfferVisibility, 1100);

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /** @param {MouseEvent} event */
  #handleOfferClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const link = target.closest('a.mfs__btn');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const url = new URL(link.href, window.location.href);
    if (url.pathname === window.location.pathname) {
      event.preventDefault();
      this.#themeDrawer?.close();
      return;
    }

    try {
      sessionStorage.setItem('magiCloseCartAfterNav', '1');
    } catch (e) {
      this.#themeDrawer?.close();
    }
    // Normal link navigation continues.
  };

  #closeAfterOfferNavigation() {
    let flagged = false;
    try {
      flagged = sessionStorage.getItem('magiCloseCartAfterNav') === '1';
      if (flagged) sessionStorage.removeItem('magiCloseCartAfterNav');
    } catch (e) {
      flagged = false;
    }
    if (!flagged) return;

    const close = () => setTimeout(() => this.#themeDrawer?.close(), 600);
    if (document.readyState === 'complete') {
      close();
    } else {
      window.addEventListener('load', close, { once: true });
    }
  }

  #scheduleOfferVisibility = () => {
    if (this.#visRaf) return;
    this.#visRaf = requestAnimationFrame(() => {
      this.#visRaf = 0;
      this.#updateOfferVisibility();
    });
  };

  /** Sets dialog[data-offer-visible] when enough of the offer card is on screen (used by the bottom nudge). */
  #updateOfferVisibility() {
    const dialog = this.#dialog;
    if (!dialog) return;
    const offer = this.querySelector('.mfs-offer');
    const summary = this.querySelector('.cart-drawer__summary');
    const content = this.querySelector('.cart-drawer__content');
    if (!offer || !summary || !content) {
      dialog.removeAttribute('data-offer-visible');
      return;
    }
    const o = offer.getBoundingClientRect();
    const s = summary.getBoundingClientRect();
    const c = content.getBoundingClientRect();
    const shown = Math.min(o.bottom, s.top) - Math.max(o.top, c.top);
    dialog.toggleAttribute('data-offer-visible', shown >= Math.min(120, o.height * 0.5));
  }

  #clearJustAdded = () => {
    this.#dialog?.removeAttribute('data-just-added');
    this.#pendingCelebrate = 0;
    this.#releaseHold();
  };

  /** Hide the offer box until the new product is on screen. */
  #holdOffer() {
    const dialog = this.#dialog;
    if (!dialog || this.#reducedMotion()) return;
    dialog.setAttribute('data-offer-hold', '');
    this.#pendingEnter = true;
    if (this.#holdTimeout) clearTimeout(this.#holdTimeout);
    // Safety net: never keep the offer hidden for long.
    this.#holdTimeout = setTimeout(() => this.#playEnter(), 6000);
  }

  #releaseHold() {
    if (this.#holdTimeout) clearTimeout(this.#holdTimeout);
    this.#holdTimeout = undefined;
    this.#pendingEnter = false;
    this.#dialog?.removeAttribute('data-offer-hold');
    if (this.#pendingCelebrate) {
      const stage = this.#pendingCelebrate;
      this.#pendingCelebrate = 0;
      if (this.#themeDrawer?.isOpen) this.#celebrate(stage);
    }
  }

  #startAdding() {
    const dialog = this.#dialog;
    if (!dialog) return;
    dialog.setAttribute('data-adding', '');
    if (this.#addingTimeout) clearTimeout(this.#addingTimeout);
    this.#addingTimeout = setTimeout(() => this.#stopAdding(), 8000);
  }

  #stopAdding() {
    if (this.#addingTimeout) clearTimeout(this.#addingTimeout);
    this.#addingTimeout = undefined;
    this.#dialog?.removeAttribute('data-adding');
  }

  #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #readStage() {
    const offer = /** @type {HTMLElement | null} */ (this.querySelector('.mfs-offer'));
    return offer ? Number(offer.dataset.stage || 0) : -1;
  }

  /** Open a space at the top (pushing "Added" + products down), then pop the offer box in. */
  #playEnter() {
    const dialog = this.#dialog;
    if (!dialog || !this.#pendingEnter) return;
    if (this.#holdTimeout) clearTimeout(this.#holdTimeout);
    this.#holdTimeout = undefined;
    dialog.removeAttribute('data-offer-hold');

    const offer = /** @type {HTMLElement | null} */ (this.querySelector('.mfs-offer'));
    const card = /** @type {HTMLElement | null} */ (this.querySelector('.mfs-offer .mfs'));
    if (!offer || !card || this.#reducedMotion() || !this.#themeDrawer?.isOpen) {
      this.#enteredAt = Date.now();
      this.#releaseHold();
      return;
    }

    const content = this.querySelector('.cart-drawer__content');
    if (content) content.scrollTop = 0;

    const cs = getComputedStyle(offer);
    const h = offer.offsetHeight;
    offer.style.overflow = 'hidden';
    const open = offer.animate(
      [
        { height: '0px', marginTop: '0px', marginBottom: '0px' },
        { height: `${h}px`, marginTop: cs.marginTop, marginBottom: cs.marginBottom },
      ],
      { duration: 520, easing: 'cubic-bezier(.3,.8,.3,1)', fill: 'backwards' }
    );
    const pop = card.animate(
      [
        { transform: 'scale(.6)', opacity: 0 },
        { transform: 'scale(1.06)', opacity: 1, offset: 0.65 },
        { transform: 'scale(.98)', opacity: 1, offset: 0.85 },
        { transform: 'scale(1)', opacity: 1 },
      ],
      { duration: 650, delay: 280, easing: 'ease-out', fill: 'backwards' }
    );
    open.finished
      .catch(() => {})
      .finally(() => {
        offer.style.overflow = '';
        this.#scheduleOfferVisibility();
      });
    pop.finished
      .catch(() => {})
      .finally(() => {
        this.#enteredAt = Date.now();
        this.#releaseHold();
      });
  }

  #checkOffer = () => {
    this.#scheduleOfferVisibility();

    const stage = this.#readStage();
    if (stage === this.#lastStage) return;

    const previous = this.#lastStage;
    this.#lastStage = stage;

    if (stage > previous && stage >= 1) {
      // Offer still hidden or popping in: celebrate right after it lands.
      if (this.#pendingEnter || this.#dialog?.hasAttribute('data-offer-hold')) {
        this.#pendingCelebrate = stage;
      } else if (this.#themeDrawer?.isOpen) {
        const wait = Math.max(0, 300 - (Date.now() - this.#enteredAt));
        setTimeout(() => this.#celebrate(stage), wait);
      }
    }
  };

  /** @param {number} stage */
  #celebrate(stage) {
    const dialog = this.#dialog;
    const card = /** @type {HTMLElement | null} */ (this.querySelector('.mfs'));
    if (!dialog || !card) return;

    card.scrollIntoView({ block: 'nearest', behavior: this.#reducedMotion() ? 'auto' : 'smooth' });
    setTimeout(this.#scheduleOfferVisibility, 500);

    if (!this.#reducedMotion()) {
      card.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.045)' }, { transform: 'scale(.99)' }, { transform: 'scale(1)' }],
        { duration: 650, easing: 'ease-out' }
      );
    }

    const d = dialog.getBoundingClientRect();
    const r = card.getBoundingClientRect();
    const layer = document.createElement('div');
    layer.className = 'mfs-burst';
    layer.style.left = `${r.left - d.left}px`;
    layer.style.top = `${r.top - d.top}px`;
    layer.style.width = `${r.width}px`;
    layer.style.height = `${r.height}px`;

    const msg = document.createElement('div');
    msg.className = 'mfs-burst__msg';
    msg.textContent = stage >= 2 ? '🎉 ₹300 OFF unlocked!' : '🎉 FREE shipping unlocked!';
    layer.appendChild(msg);

    if (!this.#reducedMotion()) {
      const colors = ['#e85f3a', '#ffd166', '#16a34a', '#3b82f6', '#f472b6', '#f59e0b'];
      for (let i = 0; i < 40; i++) {
        const bit = document.createElement('span');
        bit.className = 'mfs-burst__bit';
        const angle = Math.random() * Math.PI * 2;
        const dist = 70 + Math.random() * 130;
        bit.style.setProperty('--dx', `${Math.round(Math.cos(angle) * dist)}px`);
        bit.style.setProperty('--dy', `${Math.round(Math.sin(angle) * dist - 30)}px`);
        bit.style.setProperty('--rot', `${Math.round(Math.random() * 720 - 360)}deg`);
        bit.style.background = colors[i % colors.length];
        bit.style.animationDelay = `${Math.round(Math.random() * 120)}ms`;
        layer.appendChild(bit);
      }
    }

    dialog.appendChild(layer);
    setTimeout(() => layer.remove(), 2200);
  }

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    if (event.action === 'add') {
      this.#dialog?.setAttribute('data-just-added', '');
      this.#holdOffer();
    }

    // Defer auto-open until a source MODAL dialog (e.g. quick-add) has closed.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal) {
      if (this.#isCartEmpty()) this.#startAdding();
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (detail?.didError) {
          this.#stopAdding();
          this.#clearJustAdded();
          settle();
          return;
        }

        // Cart has re-rendered with the new product: let her see it, then bring in the offer.
        const reveal = () => setTimeout(() => this.#playEnter(), 650);

        if (!shouldAutoOpen) {
          settle();
          if (event.action === 'add' && this.#themeDrawer?.isOpen) reveal();
          else this.#releaseHold();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
          setTimeout(() => this.#stopAdding(), 1500);
          requestAnimationFrame(reveal);
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        this.#stopAdding();
        this.#clearJustAdded();
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}